// Shown when a recording stops: preview the clip, download it, or make a
// share page (/r/<id>) with a link to copy / send.
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Copy, Download, ExternalLink, Loader, Share2, Smartphone, X } from 'lucide-react';
import { supabase } from '../../config/supabase';
import { extFor, sharePageUrl, shareRecording, type FinishedRecording } from '../../services/recordingShare';
import { makeReel, type ReelMode } from '../../utils/makeReel';
import './RecordingSavedDialog.css';

const RecordingSavedDialog: React.FC<{ rec: FinishedRecording; onClose: () => void }> = ({ rec, onClose }) => {
  const url = useMemo(() => URL.createObjectURL(rec.blob), [rec.blob]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  const [state, setState] = useState<'idle' | 'uploading' | 'done' | 'error'>('idle');
  const [pct, setPct] = useState(0);
  const [err, setErr] = useState('');
  const [link, setLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [reelMode, setReelMode] = useState<ReelMode>('fit');
  const [reel, setReel] = useState<'idle' | 'making' | 'error'>('idle');
  const [reelPct, setReelPct] = useState(0);
  const reelAbort = React.useRef<AbortController | null>(null);
  useEffect(() => () => reelAbort.current?.abort(), []);
  const busy = state === 'uploading' || reel === 'making';

  useEffect(() => { supabase.auth.getSession().then(({ data }) => setSignedIn(!!data.session)); }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && state !== 'uploading' && reel !== 'making') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, state, reel]);

  const download = () => {
    const a = document.createElement('a');
    a.href = url; a.download = `gathrxr-recording-${Date.now()}.${extFor(rec.mime)}`; a.click();
  };
  const downloadReel = async () => {
    const ac = new AbortController(); reelAbort.current = ac;
    setReel('making'); setReelPct(0);
    try {
      const out = await makeReel(rec.blob, { mode: reelMode, durationS: rec.durationS, title: rec.worldName, onProgress: setReelPct, signal: ac.signal });
      const a = document.createElement('a'); const u = URL.createObjectURL(out.blob);
      a.href = u; a.download = `gathrxr-reel-${Date.now()}.${extFor(out.mime)}`; a.click();
      setTimeout(() => URL.revokeObjectURL(u), 5000);
      setReel('idle');
    } catch (e: any) {
      setReel(e?.name === 'AbortError' ? 'idle' : 'error');
    }
  };
  const share = async () => {
    setState('uploading'); setErr(''); setPct(0);
    try {
      const id = await shareRecording(rec, setPct);
      setLink(sharePageUrl(id)); setState('done');
    } catch (e: any) {
      setErr(e?.message || 'Could not share this recording.'); setState('error');
    }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* ignore */ }
  };
  const nativeShare = async () => {
    try { await navigator.share({ title: rec.worldName ? `${rec.worldName} on GathrXR` : 'My GathrXR clip', url: link }); } catch { /* cancelled */ }
  };
  const mb = (rec.blob.size / 1024 / 1024).toFixed(1);

  return createPortal(
    <div className="gx-recdlg-backdrop" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="gx-recdlg" role="dialog" aria-modal="true" aria-labelledby="gx-recdlg-title">
        <header className="gx-recdlg-head">
          <h2 id="gx-recdlg-title">Your recording</h2>
          <button className="gx-recdlg-x" onClick={onClose} disabled={busy} aria-label="Close"><X size={18} /></button>
        </header>
        <video className="gx-recdlg-video" src={url} controls playsInline />
        <p className="gx-recdlg-meta">{Math.round(rec.durationS)} s · {mb} MB{rec.worldName ? ` · ${rec.worldName}` : ''}</p>

        {state === 'done' ? (
          <div className="gx-recdlg-share">
            <p className="gx-recdlg-ok"><Check size={16} /> Share page ready. Anyone with the link can watch it, then visit the world or join GathrXR.</p>
            <div className="gx-recdlg-link">
              <input readOnly value={link} onFocus={(e) => e.currentTarget.select()} aria-label="Share link" />
              <button className="gx-recdlg-btn" onClick={copy}>{copied ? <Check size={16} /> : <Copy size={16} />}{copied ? 'Copied' : 'Copy'}</button>
            </div>
            <div className="gx-recdlg-actions">
              {typeof navigator.share === 'function' && <button className="gx-recdlg-btn" onClick={nativeShare}><Share2 size={16} /> Send…</button>}
              <a className="gx-recdlg-btn" href={link} target="_blank" rel="noreferrer"><ExternalLink size={16} /> Open page</a>
              <button className="gx-recdlg-btn" onClick={download}><Download size={16} /> Download</button>
            </div>
          </div>
        ) : (
          <>
            {state === 'error' && <p className="gx-recdlg-err" role="alert">{err}</p>}
            {state === 'uploading' && (
              <div className="gx-recdlg-progress" aria-live="polite">
                <div className="gx-recdlg-bar"><span style={{ width: `${pct}%` }} /></div>
                <small>Uploading… {pct}%</small>
              </div>
            )}
            <div className="gx-recdlg-actions">
              <button className="gx-recdlg-btn" onClick={download} disabled={state === 'uploading'}><Download size={16} /> Download</button>
              {signedIn === false ? (
                <a className="gx-recdlg-btn is-primary" href={`/login?next=${encodeURIComponent(window.location.pathname)}`}><Share2 size={16} /> Sign in to share</a>
              ) : (
                <button className="gx-recdlg-btn is-primary" onClick={share} disabled={state === 'uploading' || signedIn === null}>
                  {state === 'uploading' ? <Loader size={16} className="spin" /> : <Share2 size={16} />} Create share page
                </button>
              )}
            </div>
          </>
        )}

        <section className="gx-recdlg-reel" aria-label="Reel">
          <div className="gx-recdlg-reel-head">
            <span><Smartphone size={16} /> Reel (9:16, 1080×1920)</span>
            <div className="gx-recdlg-seg" role="radiogroup" aria-label="Reel framing">
              {(['fit', 'fill'] as ReelMode[]).map((m) => (
                <button key={m} role="radio" aria-checked={reelMode === m} className={reelMode === m ? 'is-on' : ''} disabled={reel === 'making'} onClick={() => setReelMode(m)}>
                  {m === 'fit' ? 'Whole frame' : 'Crop to fill'}
                </button>
              ))}
            </div>
          </div>
          {reel === 'making' ? (
            <div className="gx-recdlg-progress" aria-live="polite">
              <div className="gx-recdlg-bar"><span style={{ width: `${reelPct}%` }} /></div>
              <small>Making your reel… {reelPct}% <button className="gx-recdlg-linkbtn" onClick={() => reelAbort.current?.abort()}>Cancel</button></small>
            </div>
          ) : (
            <div className="gx-recdlg-actions">
              {reel === 'error' && <span className="gx-recdlg-err">Couldn't make the reel in this browser.</span>}
              <button className="gx-recdlg-btn" onClick={downloadReel} disabled={busy}><Download size={16} /> Download reel</button>
            </div>
          )}
        </section>
      </div>
    </div>,
    document.body,
  );
};

export default RecordingSavedDialog;
